import { ClipboardList, FileText, LayoutGrid, LayoutList, Plus, Stamp } from 'lucide-react';
import { Fab } from '@/components/ui/Fab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  compartilharRecurso,
  copiarModelo,
  createModelo,
  deleteModelo,
  fetchCompartilhamentos,
  fetchEscritorios,
  fetchModelos,
  removeCompartilhamento,
  type CompartilhamentoInfo,
  type EscritorioResumo,
} from '@/api/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BarraDaLista } from '@/components/ui/barra-da-lista';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/EmptyState';
import { OrigemFilterBar } from '@/components/ui/OrigemFilterBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { BuscaInteligente } from '@/components/ui/smart-search';
import type { TagResumo } from '@/api/tags';
import { useTags } from '@/hooks/useTags';
import { casaFacetas, facetasDoFiltro } from '@/lib/tags/agrupar';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { useAuth } from '@/hooks/useAuth';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { useResourcePermissions } from '@/hooks/useResourcePermissions';
import { useSmartSearch } from '@/hooks/useSmartSearch';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';
import type { SmartSearchFields } from '@/lib/search/smartSearch';

import { ModeloDeleteDialog } from './ModeloDeleteDialog';
import { ModeloGrid } from './ModeloGrid';
import { ModeloShareDialog } from './ModeloShareDialog';
import { ModeloTable } from './ModeloTable';
import { ModeloUsarDialog } from './ModeloUsarDialog';
import { ModeloViewDialog } from './ModeloViewDialog';

// --- Tipos e helpers exportados para componentes irmãos ---

export interface PeticaoModelo {
  id: string;
  titulo: string;
  corpo: string;
  variaveis?: Record<string, string>;
  usuario_id?: string | null;
  created_at?: string;
  updated_at?: string;
  escritorio_id?: string | null;
  origem?: string | null;
  escritorio_nome?: string | null;
  compartilhado_por?: string | null;
  /** Tags que quem consulta vê (PLANO_TAGS). */
  tags?: TagResumo[];
}

export function extractPlainText(html: string): string {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();
  } catch {
    return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  }
}

// O texto puro do corpo sai do DOMParser, que não é de graça: guardado por modelo
// (o objeto que veio da query), a busca não refaz o parse a cada tecla.
const corpoSemTags = new WeakMap<PeticaoModelo, string>();
function textoDoCorpo(modelo: PeticaoModelo): string {
  let texto = corpoSemTags.get(modelo);
  if (texto === undefined) {
    texto = extractPlainText(modelo.corpo);
    corpoSemTags.set(modelo, texto);
  }
  return texto;
}

/** A busca inteligente dos modelos: título e o texto do corpo, sem as tags. */
const MODELO_SEARCH_FIELDS: SmartSearchFields<PeticaoModelo> = {
  text: (m) => `${m.titulo}\n${textoDoCorpo(m)}`,
};

const SEM_MODELOS: PeticaoModelo[] = [];

// --- Componente principal ---

function ModelosPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  // View mode com persistência em localStorage
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    const saved = localStorage.getItem('modelos-view-mode');
    if (saved === 'grid' || saved === 'list') return saved;
    return window.innerWidth < 640 ? 'grid' : 'list';
  });
  const changeViewMode = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('modelos-view-mode', mode);
  };

  // Filtros
  const [searchQuery, setSearchQuery] = useState('');
  const [origemFilter, setOrigemFilter] = useState<string | undefined>(undefined);
  // Filtro por tags (PLANO_TAGS D7) no cliente, como a busca: a lista vem inteira.
  const [filtroTags, setFiltroTags] = useState<string[]>([]);
  const tagsQuery = useTags();
  // Modelo com o diálogo de tags aberto fica na lista mesmo saindo do filtro.
  const [emEdicao, setEmEdicao] = useState<string | null>(null);

  // Queries
  const { data: escritorios, error: escritoriosError } = useQuery<EscritorioResumo[]>({
    queryKey: ['escritorios'],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
  });
  const hasEscritorios = (escritorios && escritorios.length > 0) || !!escritoriosError;

  const { canWrite, canShare } = useResourcePermissions({
    escritorios, isAdmin: user?.role === 'admin', currentUserId: user?.id,
  });

  const { data, isLoading, error } = useQuery<PeticaoModelo[]>({
    queryKey: [...queryKeys.modelos.all, { origem: origemFilter }],
    queryFn: () => fetchModelos({ origem: origemFilter }),
    meta: queryPersistMeta.nonEmpty,
  });

  // As tags (facetas) e, por cima, a busca inteligente — as duas no navegador: a lista
  // vem inteira (filtrada só pela origem).
  const facetas = useMemo(
    () => facetasDoFiltro(filtroTags, tagsQuery.tagsPorId),
    [filtroTags, tagsQuery.tagsPorId],
  );
  const porTags = useMemo(
    () => (data ?? SEM_MODELOS).filter((modelo) => modelo.id === emEdicao || casaFacetas(modelo.tags, facetas)),
    [data, emEdicao, facetas],
  );
  const search = useSmartSearch(porTags, searchQuery, MODELO_SEARCH_FIELDS);
  const filteredData = data ? search.results : undefined;

  // --- Dialog state ---
  const [usarOpen, setUsarOpen] = useState(false);
  const [usarModelo, setUsarModelo] = useState<PeticaoModelo | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewModelo, setViewModelo] = useState<PeticaoModelo | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteModelo_, setDeleteModelo] = useState<PeticaoModelo | null>(null);

  // Share state
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState<PeticaoModelo | null>(null);
  const [shareEmail, setShareEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const [shareList, setShareList] = useState<CompartilhamentoInfo[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  // --- Mutations ---

  const getModelTitle = (v: { titulo: string }) => v.titulo;

  // Criar mutation permanece só para "duplicar" (criar/editar migraram p/ ModeloEditorPage).
  const createMutation = useOfflineMutation({
    mutationFn: createModelo,
    outbox: {
      type: 'modelo_criar',
      getPayload: (v) => ({ method: 'POST', url: '/modelos-peticao', body: v }),
      getDescription: (v) => `Duplicar modelo "${getModelTitle(v)}"`,
    },
    invalidateKeys: [[...queryKeys.modelos.all], [...queryKeys.secretario.modelosAll]],
    onSuccess: () => showSuccess('Modelo criado com sucesso!'),
    onError: (error) => notifyMutationError(error, showError),
  });

  const deleteMutation = useOfflineMutation({
    mutationFn: deleteModelo,
    outbox: {
      type: 'modelo_deletar',
      getPayload: (id) => ({ method: 'DELETE', url: `/modelos-peticao/${id}` }),
      getDescription: () => 'Mover modelo para lixeira',
    },
    invalidateKeys: [[...queryKeys.modelos.all], [...queryKeys.secretario.modelosAll]],
    onSuccess: () => {
      setDeleteOpen(false);
      setDeleteModelo(null);
      showSuccess('Modelo movido para a lixeira');
    },
    onQueued: () => {
      setDeleteOpen(false);
      setDeleteModelo(null);
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  // --- Handlers ---

  const handleUsarOpen = (modelo: PeticaoModelo) => {
    setUsarModelo(modelo);
    setUsarOpen(true);
  };

  // Modelo que o usuário não altera (do sistema, compartilhado) se edita numa
  // cópia DELE: o "Editar" cria a cópia e abre o editor nela, em vez de
  // esconder o botão e deixar o caminho (Duplicar, achar a cópia, editar) por
  // conta de quem só queria ajustar uma cláusula.
  const copiarParaEditar = useMutation({
    mutationFn: (modelo: PeticaoModelo) => copiarModelo(modelo.id),
    onSuccess: (copia) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.modelos.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.modelosAll });
      showSuccess('Criamos uma cópia sua para editar.');
      navigate(`/app/modelos/${copia.id}/editar`);
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  const handleEditOpen = (modelo: PeticaoModelo) => {
    if (canWrite(modelo.origem, modelo.escritorio_id)) {
      navigate(`/app/modelos/${modelo.id}/editar`);
    } else if (!copiarParaEditar.isPending) {
      // Sem a trava, dois cliques criavam duas cópias.
      copiarParaEditar.mutate(modelo);
    }
  };

  const handleViewOpen = (modelo: PeticaoModelo) => {
    setViewModelo(modelo);
    setViewOpen(true);
  };

  const handleDeleteOpen = (modelo: PeticaoModelo) => {
    setDeleteModelo(modelo);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModelo_) return;
    await deleteMutation.mutateAsync(deleteModelo_.id);
  };

  const handleDuplicate = async (modelo: PeticaoModelo) => {
    await createMutation.mutateAsync({
      titulo: `${modelo.titulo} (cópia)`,
      corpo: modelo.corpo,
      variaveis: modelo.variaveis ?? {},
    });
  };

  const handleOpenShare = async (modelo: PeticaoModelo) => {
    setShareTarget(modelo);
    setShareEmail('');
    setShareOpen(true);
    setLoadingShares(true);
    try {
      const shares = await fetchCompartilhamentos('MODELO_PETICAO', modelo.id);
      setShareList(shares);
    } catch (err) {
      notifyMutationError(err, showError);
      setShareOpen(false);
      setShareList([]);
    } finally {
      setLoadingShares(false);
    }
  };

  const handleShare = async () => {
    if (!shareTarget || !shareEmail.trim()) return;
    setSharing(true);
    try {
      await compartilharRecurso({ recurso_tipo: 'MODELO_PETICAO', recurso_id: shareTarget.id, email: shareEmail });
      showSuccess('Modelo compartilhado com sucesso');
      setShareEmail('');
      const shares = await fetchCompartilhamentos('MODELO_PETICAO', shareTarget.id);
      setShareList(shares);
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setSharing(false);
    }
  };

  const handleRevokeShare = async (shareId: string) => {
    try {
      await removeCompartilhamento(shareId);
      showSuccess('Compartilhamento revogado');
      if (shareTarget) {
        const shares = await fetchCompartilhamentos('MODELO_PETICAO', shareTarget.id);
        setShareList(shares);
      }
    } catch (err) {
      notifyMutationError(err, showError);
    }
  };

  // --- Render ---

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ClipboardList />}
        title="Modelos de Petição"
        subtitle="Crie e gerencie modelos reutilizáveis para suas petições"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/app/configuracoes/timbrado')}
              className="hidden sm:inline-flex"
            >
              <Stamp className="h-4 w-4 mr-2" />
              Papel timbrado
            </Button>
            <Button
              onClick={() => navigate('/app/modelos/novo')}
              className="hidden sm:inline-flex"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Modelo
            </Button>
          </div>
        }
      />

      <Fab icon={<Plus className="h-6 w-6" />} label="Novo Modelo" onClick={() => navigate('/app/modelos/novo')} />

      {/* A barra da lista (docs/PLANO_BARRA_DE_LISTA.md): sem abas, a busca ocupa a
          linha; embaixo, a Origem, as Tags e, à direita, a alternância lista/grade. */}
      <BarraDaLista
        busca={
          <BuscaInteligente
            semNumero
            value={searchQuery}
            onChange={setSearchQuery}
            strictness={search.strictness}
            onStrictnessChange={search.setStrictness}
            resultCount={search.results.length}
            totalCount={search.total}
            placeholder="Buscar por título ou texto do modelo…"
          />
        }
        filtros={
          <>
            {hasEscritorios && (
              <OrigemFilterBar
                escritorios={escritorios ?? []}
                value={origemFilter}
                onChange={setOrigemFilter}
                includeSistema
                includeCompartilhados
              />
            )}
          </>
        }
        filtrosAtivos={origemFilter ? 1 : 0}
        filtroDeTags={{ value: filtroTags, onChange: setFiltroTags }}
        acoes={
          <div role="group" aria-label="Exibição" className="flex rounded-md border border-input">
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            aria-label="Ver em lista"
            aria-pressed={viewMode === 'list'}
            className="h-11 w-11 rounded-r-none sm:h-9 sm:w-9"
            onClick={() => changeViewMode('list')}
          >
            <LayoutList className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            aria-label="Ver em grade"
            aria-pressed={viewMode === 'grid'}
            className="h-11 w-11 rounded-l-none sm:h-9 sm:w-9"
            onClick={() => changeViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          </div>
        }
      />

      {/* Erro inline */}
      {(error || escritoriosError) && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error || escritoriosError).message}</AlertDescription>
        </Alert>
      )}

      {/* Conteúdo principal */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size={32} label="Carregando modelos" />
        </div>
      ) : filteredData && filteredData.length > 0 ? (
        viewMode === 'list' ? (
          <ModeloTable
            modelos={filteredData}
            canWrite={canWrite}
            canShare={canShare}
            onUsar={handleUsarOpen}
            onView={handleViewOpen}
            onEdit={handleEditOpen}
            onDuplicate={handleDuplicate}
            onShare={handleOpenShare}
            onDelete={handleDeleteOpen}
            onTagsAbertas={setEmEdicao}
          />
        ) : (
          <ModeloGrid
            modelos={filteredData}
            canWrite={canWrite}
            canShare={canShare}
            onUsar={handleUsarOpen}
            onView={handleViewOpen}
            onEdit={handleEditOpen}
            onDuplicate={handleDuplicate}
            onShare={handleOpenShare}
            onDelete={handleDeleteOpen}
            onTagsAbertas={setEmEdicao}
          />
        )
      ) : (
        <EmptyState
          icon={<FileText />}
          title={searchQuery || filtroTags.length ? 'Nenhum modelo encontrado' : 'Nenhum modelo cadastrado'}
          description={searchQuery || filtroTags.length ? 'Tente ajustar a busca ou o filtro de tags' : 'Crie seu primeiro modelo de petição para agilizar seu trabalho'}
          action={!searchQuery && !filtroTags.length ? { label: 'Criar Primeiro Modelo', onClick: () => navigate('/app/modelos/novo') } : undefined}
        />
      )}

      {/* Dialogs */}
      <ModeloUsarDialog
        open={usarOpen}
        onOpenChange={setUsarOpen}
        modelo={usarModelo}
      />

      <ModeloViewDialog
        open={viewOpen}
        onOpenChange={setViewOpen}
        modelo={viewModelo}
        onEdit={handleEditOpen}
        onDuplicate={handleDuplicate}
      />

      <ModeloDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        modelo={deleteModelo_}
        onConfirm={handleDeleteConfirm}
        isPending={deleteMutation.isPending}
      />

      <ModeloShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        modelo={shareTarget}
        shareEmail={shareEmail}
        onShareEmailChange={setShareEmail}
        onShare={handleShare}
        sharing={sharing}
        shareList={shareList}
        loadingShares={loadingShares}
        onRevokeShare={handleRevokeShare}
      />
    </div>
  );
}

export default ModelosPage;
